<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { useSelectionStore } from '@/stores/selection'
import { useNgMarkStore } from '@/stores/ngMark'
import { useLayoutStore, CARD_LABELS } from '@/stores/layout'
import { levelBucket } from '@/utils/ngHighlight'
import {
  useOfferDetail,
  useInitMasters,
  useRelatedFileCounts,
} from '@/api/queries'
import { openExternal } from '@/utils/links'
import { runRelatedFileBat } from '@/utils/localExec'
import NgMarkedText from './NgMarkedText.vue'
import NgLevelFilter from './NgLevelFilter.vue'
import MallCategoryPanel from './MallCategoryPanel.vue'
import PreviewResizer from './PreviewResizer.vue'
import ProductImagePanel from './ProductImagePanel.vue'
import JudgeLogModal from './JudgeLogModal.vue'
import ProductEditModal from './ProductEditModal.vue'

const selection = useSelectionStore()
const ngMark = useNgMarkStore()
const layout = useLayoutStore()
const asinRef = computed(() => selection.selectedAsin)
const sellerIdRef = computed(() => selection.selectedSellerId)
const selectedNgWord = computed(() => selection.selectedNgWord)

const { data: detail, isLoading } = useOfferDetail(asinRef, sellerIdRef)
const { data: masters } = useInitMasters()

const product = computed(() => detail.value?.product)
const offer = computed(() => detail.value?.offer)

// ---- 商品を切り替えたら、商品詳細の先頭までスクロールする ----
// (一覧を下までスクロールした状態で商品を選ぶと、詳細の上部が見えないため)
const detailRoot = ref<HTMLElement | null>(null)

// 上部ヘッダー(.topbar)がposition:stickyで58px分居座っているため、
// 単純に要素の先頭へスクロールするとその分がヘッダーの裏に隠れてしまう
// (親ASINが見えなくなる)。ヘッダーの高さ分だけ手前で止める。
const STICKY_HEADER_HEIGHT = 58

watch(
  () => selection.selectedAsin,
  async (asin) => {
    if (!asin) return
    await nextTick()
    const el = detailRoot.value
    if (!el) return
    // 現在のスクロール位置 + 要素の画面内Y座標 - ヘッダー高さ = 目的のスクロール位置
    const top = window.scrollY + el.getBoundingClientRect().top - STICKY_HEADER_HEIGHT
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
  },
)

const childAsin = computed(() => selection.selectedAsin ?? '')
// 画面表示中のセラーID(関連ファイルbatの第2引数)
const sellerId = computed(() => selection.selectedSellerId ?? '')
const parentAsin = computed(() => detail.value?.parentAsin ?? '')

// 関連ファイル数バッジ。画面表示時・商品切り替え時に取得するだけで、
// リンククリックに連動した再取得は行わない(値はbat実行時のスナップショット)。
const { data: fileCounts } = useRelatedFileCounts(
  computed(() => detail.value?.parentAsin ?? null),
  computed(() => selection.selectedAsin),
  computed(() => selection.selectedSellerId),
)

/**
 * ハイライトを全消しする。
 * 選択中の語のマークしか特定できないため、消す側は本文全体を対象にする。
 */
function clearHighlight() {
  detailRoot.value
    ?.querySelectorAll('.ng-mark-selected')
    .forEach((el) => el.classList.remove('ng-mark-selected'))
}

/**
 * 選択中のNGワードを含むマークをハイライトする。
 * スクロール移動(前へ/次へ)は行わない。該当箇所が複数あっても色分けはせず、
 * すべて同じ色にする。
 */
function applyHighlight() {
  clearHighlight()
  if (!detailRoot.value || !selectedNgWord.value) return
  detailRoot.value.querySelectorAll<HTMLElement>('.ng-mark').forEach((el) => {
    if ((el.dataset.ngWords ?? '').split('\u001f').includes(selectedNgWord.value!)) {
      el.classList.add('ng-mark-selected')
    }
  })
}

// 語の選択や商品の切り替えでハイライトを貼り直す
// (拡大表示のリセットはここではなく、商品切り替えのwatchで行う)
watch(
  [selectedNgWord, product],
  async () => {
    await nextTick()
    applyHighlight()
  },
  { immediate: true },
)

const productLinks = computed(() => [
  { label: '商品の詳細(Amazon)', run: () => openExternal(`https://www.amazon.co.jp/dp/${childAsin.value}`) },
  { label: '出品者の一覧', run: () => openExternal(`https://www.amazon.co.jp/gp/offer-listing/${childAsin.value}`) },
  { label: '商品確認(DQ)', run: () => openExternal(`http://192.168.10.47/query/190?詳細=${childAsin.value}`) },
])

// bat/exe起動系(ブラウザから直接起動不可 → パスをコピー)
// ジャッジ待ち申請は、右下のボタン群(ActionBar)に移した(チェックした商品をまとめて申請できる)
const showJudgeLogModal = ref(false)

/** バッジのツールチップ用。「いつ時点の件数か」を示す */
function formatBadgeDate(iso: string) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

// ---- 商品属性カード内のNGワードチップ ----
// 語だけを重複なく、レベルの高い順に並べる(説明文は左のリスト/ホバーで見る)
const ngWordChips = computed(() => {
  const seen = new Set<string>()
  return ngMark.ngWords
    .filter((w) => {
      if (seen.has(w.word)) return false
      seen.add(w.word)
      return true
    })
    .sort((a, b) => b.level - a.level)
})

/** 同じ語をもう一度押したら選択解除(全ヒットのハイライトに戻す) */
function toggleNgChip(word: string) {
  selection.selectNgWord(selection.selectedNgWord === word ? null : word)
}

// 商品情報編集(商品属性カード内のボタンから開く)
const showEditModal = ref(false)
function openProductEdit() {
  showEditModal.value = true
}

const toolLinks = computed(() => [
  {
    label: '関連ファイル(親ASIN)',
    icon: 'folder',
    run: () => runRelatedFileBat('OpenProduct.bat', parentAsin.value, sellerId.value),
    disabled: !parentAsin.value,
    tooltip: '関連ファイルのフォルダを開きます(拡張機能が無い場合はパスをコピーします)',
    badge: fileCounts.value?.parentAsin ?? null,
  },
  {
    label: '関連ファイル(子ASIN)',
    icon: 'folder',
    run: () => runRelatedFileBat('OpenProduct.bat', childAsin.value, sellerId.value),
    disabled: false,
    tooltip: '関連ファイルのフォルダを開きます(拡張機能が無い場合はパスをコピーします)',
    badge: fileCounts.value?.childAsin ?? null,
  },
  {
    label: '審査履歴',
    icon: 'history',
    run: () => { showJudgeLogModal.value = true },
    disabled: false,
    tooltip: 'この商品の過去の審査結果をモーダルで表示します',
  },
])

// ---- 消費税率 ----
// VB.NET版: offer.taxId → mtax.TaxRate。0 または 0.1 は「10%」、それ未満は軽減税率(8%等)。
// ドロップダウンで選択した税率IDは、審査時/保存時にoffer.taxIdとして永続化される。
const selectedTaxId = ref<number | null>(null)

watch(
  () => detail.value?.offer,
  (o) => {
    selectedTaxId.value = o?.taxId ?? null
  },
)

const taxOptions = computed(() => masters.value?.taxMaster ?? [])

function taxLabel(rate: number): string {
  return `${Math.round(rate * 100)}%`
}

// 現在の税率表示(選択中のtaxIdに対応する税率)
const currentTaxDisplay = computed(() => {
  const opt = taxOptions.value.find((t) => t.id === selectedTaxId.value)
  if (opt) return taxLabel(opt.taxRate)
  return '10%' // 既定
})

// 親(ActionBar)へ選択中taxIdを伝えるため、storeに保持する
watch(selectedTaxId, (v) => {
  selection.selectedTaxId = v
})

/** カテゴリ未設定/不明の判定。VB版は "" または "Unknown" を危険色にしていた */
const isCategoryUnknown = computed(() => {
  const c = product.value?.CategoryCode
  return !c || c === 'Unknown'
})

function ratingLabel() {
  if (!product.value?.Rating) return null
  return `★ ${product.value.Rating.toFixed(1)}`
}

/**
 * 幅/長/高が0または未設定の場合は「-」表示にする。
 * amazon.product の値をそのまま表示しているだけで、フロント側の未実装ではない。
 */
function dim(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) return '-'
  return String(value)
}

function fulfillmentLabel(code: string | null | undefined): string {
  if (code === 'A') return '📦 FBA配送'
  if (code === 'M') return '🚚 自己発送'
  return code ? `📦 ${code}` : '-'
}
</script>

<template>
  <div class="detail-scroll" ref="detailRoot">
    <!-- NGワードのヒット箇所ナビ(NGワード選択中のみ表示) -->
    <div class="detail-wrap">
      <div v-if="!selection.selectedAsin" class="empty-state">左のリストから商品を選択してください</div>
      <div v-else-if="isLoading" class="empty-state"><span class="spinner"></span> 読み込み中…</div>
      <template v-else>
        <!-- カードの並び順は利用者が変更できる(layout.cardOrder)。
             ここでは順序に従って該当ブロックを描画する。 -->
        <template v-for="(key, cardIdx) in layout.cardOrder" :key="key">
        <div class="card-slot" :class="{ 'card-slot--fill': key === 'compare' }">
          <!-- 並べ替え操作。既定では隠しておき、カードにカーソルを載せたときだけ出す
               (常時表示だと審査作業の視界を邪魔するため) -->
          <div class="card-slot-tools">
            <button
              class="card-move"
              title="上へ移動"
              :disabled="cardIdx === 0"
              @click="layout.moveCard(key, -1)"
            >▲</button>
            <span class="card-move-label">{{ CARD_LABELS[key] }}</span>
            <button
              class="card-move"
              title="下へ移動"
              :disabled="cardIdx === layout.cardOrder.length - 1"
              @click="layout.moveCard(key, 1)"
            >▼</button>
          </div>

        <template v-if="key === 'header'">
          <div class="detail-header">
            <!-- FBAバッジ・ASIN・各リンクをすべて1行に並べる(縦積みにしない) -->
            <div class="dh-row">
              <span class="badge badge-fba">{{ fulfillmentLabel(offer?.FulfillmentChannelCode) }}</span>
              <span class="dh-id-row"><span class="lbl">親ASIN</span><span class="chip-id">{{ detail?.parentAsin }}</span></span>
              <span class="dh-id-row"><span class="lbl">子ASIN</span><span class="chip-id">{{ selection.selectedAsin }}</span></span>

              <a v-for="lnk in productLinks" :key="lnk.label" class="qlink" @click="lnk.run()">
                <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                {{ lnk.label }}
              </a>
              <span class="detail-links-sep"></span>
              <a
                v-for="lnk in toolLinks"
                :key="lnk.label"
                class="qlink qlink-tool"
                :class="{ disabled: lnk.disabled }"
                :title="lnk.tooltip"
                @click="!lnk.disabled && lnk.run()"
              >
                <!-- 関連ファイル系は左ペインのセラーと同じフォルダアイコン、
                     ジャッジ待ち申請はチェックボックスのアイコンにする -->
                <svg v-if="lnk.icon === 'folder'" class="icon icon-xs" viewBox="0 0 24 24"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                <svg v-else-if="lnk.icon === 'check'" class="icon icon-xs" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              <svg v-else class="icon icon-xs" viewBox="0 0 24 24"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><polyline points="12 7 12 12 15 14"/></svg>
                {{ lnk.label }}
                <!-- 関連ファイル数(FileCounter.exe がDBへ書いた件数)。
                     行が無い場合(未計測)はバッジを出さない -->
                <span
                  v-if="lnk.badge"
                  class="file-badge"
                  :class="{ zero: lnk.badge.fileCount === 0 }"
                  :title="`関連ファイル ${lnk.badge.fileCount} 件`
                    + (lnk.badge.updateDate ? `（${formatBadgeDate(lnk.badge.updateDate)} 時点）` : '')"
                >{{ lnk.badge.fileCount }}</span>
              </a>
            </div>
          </div>

          <!-- 商品画像は商品名と同じ全幅で置く(枚数が多くても横スクロールしにくいように) -->
        </template>

        <template v-if="key === 'images'">
          <ProductImagePanel :asin="selection.selectedAsin" region-code="jp" />
        </template>

        <template v-if="key === 'title'">
          <!-- 商品名は画像の直下に置く。画像と見比べながら読む場面が多いため、
               ヘッダー(ASIN・リンク)から切り離してここへ移した。 -->
          <div class="card product-title-card">
            <div class="dh-title">
              <NgMarkedText :text="product?.Title" :ng-words="ngMark.ngWords" field="title" />
            </div>
          </div>
        </template>

        <template v-if="key === 'compare'">
          <!-- 対比ゾーン: 左=商品属性(可変) / 右=カテゴリ(可変)。
               境目のハンドルで自由に幅を調整できる(layout.previewRatio)。 -->
          <div
            class="detail-compare"
            :style="{ '--preview-ratio': layout.previewRatio + '%' }"
          >
          <div class="detail-main">

          <div class="card">
            <div class="cat-edit-bar">
              <button class="btn btn-ghost btn-sm" title="この画面のまま商品名・商品属性を編集します" @click="openProductEdit">
                <svg class="icon icon-xs" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                商品情報を編集
              </button>
              <NgLevelFilter :ng-words="ngMark.ngWords" />
            </div>
            <!-- NGワードのヒット一覧(語のみ)。左のリストは説明文が長く目線移動が大きいため、
                 本文のすぐ上にも語だけを並べておく。クリックで本文の該当箇所へジャンプし、
                 ハイライトされる(左リストの選択と同じ仕組みを共有している)。 -->
            <div v-if="ngWordChips.length" class="ng-chip-row">
              <span class="ng-chip-label">NGワード</span>
              <button
                v-for="w in ngWordChips"
                :key="w.word"
                class="ng-chip"
                :class="[`lv${levelBucket(w.level)}`, { active: selectedNgWord === w.word }]"
                :title="`Lv.${w.level}　クリックで本文の該当箇所をハイライト`"
                @click="toggleNgChip(w.word)"
              >{{ w.word }}</button>

              <span v-if="selectedNgWord" class="ng-chip-nav">
                <button class="ng-nav-close" title="ハイライトを解除" @click="selection.selectNgWord(null)">✕</button>
              </span>
            </div>

            <div class="attr-grid">
              <!-- VB版 TextBoxCategory 相当。未設定 or "Unknown" は警告色で示す
                   (カテゴリ不明はジャッジ待ち申請で却下される要因になるため) -->
              <div class="attr-item">
                <span class="k">カテゴリ</span>
                <span class="v" :class="{ 'attr-danger': isCategoryUnknown }">
                  {{ product?.CategoryCode || '未設定' }}
                </span>
              </div>
              <div class="attr-item"><span class="k">ブランド</span><span class="v">{{ product?.Brand }}</span></div>
              <div class="attr-item"><span class="k">モデル</span><span class="v">{{ product?.Model }}</span></div>
              <div class="attr-item"><span class="k">カラー</span><span class="v">{{ product?.Color }}</span></div>
              <div class="attr-item"><span class="k">サイズ</span><span class="v">{{ product?.Size }}</span></div>
              <div class="attr-item"><span class="k">評価</span><span class="v">{{ ratingLabel() ?? '評価なし' }}</span></div>
              <div class="attr-item"><span class="k">EAN</span><span class="v attr-ean">{{ product?.EAN }}</span></div>
              <div class="attr-item">
                <span class="k">消費税率</span>
                <span class="v">
                  <select v-if="taxOptions.length" v-model.number="selectedTaxId" class="tax-select" :class="{ reduced: currentTaxDisplay !== '10%' }">
                    <option v-for="t in taxOptions" :key="t.id" :value="t.id">{{ taxLabel(t.taxRate) }}</option>
                  </select>
                  <span v-else>{{ currentTaxDisplay }}</span>
                </span>
              </div>
            </div>

            <div class="longtext-block" v-if="product?.Features">
              <div class="k">特徴</div>
              <div class="v v-readable">
                <NgMarkedText :text="product?.Features" :ng-words="ngMark.ngWords" preserve-whitespace field="features" />
              </div>
            </div>

            <div class="longtext-block" v-if="product?.EditorialReview">
              <div class="k">商品説明</div>
              <div class="v v-readable">
                <NgMarkedText :text="product?.EditorialReview" :ng-words="ngMark.ngWords" preserve-whitespace field="description" />
              </div>
            </div>

            <div class="dims-row">
              <div class="attr-item">
                <span class="k">幅 × 長 × 高 (cm)</span>
                <span class="v">{{ dim(product?.Width) }} × {{ dim(product?.Length) }} × {{ dim(product?.Height) }}</span>
              </div>
            </div>
          </div>

          </div>

          <!-- 商品属性とカテゴリの境目。ドラッグで幅を変えられる -->
          <PreviewResizer />

          <!-- 右カラムはカテゴリ専用。可変幅にし、カテゴリのパスが長くて
               見切れる場合は、この中だけ横スクロールさせる。 -->
          <aside class="detail-side">
            <MallCategoryPanel />
          </aside>

          </div>
        </template>

        </div>
        </template>
      </template>
    </div>

    <JudgeLogModal
      v-if="showJudgeLogModal"
      @close="showJudgeLogModal = false"
    />
    <ProductEditModal
      v-if="showEditModal && childAsin"
      :asin="childAsin"
      region-code="jp"
      @close="showEditModal = false"
    />
  </div>
</template>
